<template>
  <va-dropdown v-if="companies.length > 1" :offset="[13, 0]" class="company-dropdown" stick-to-edges>
    <template #anchor>
      <va-button preset="secondary" color="primary" class="px-2">
        <va-icon name="business" class="mr-2" />
        <span class="hidden sm:inline">{{ currentCompanyName }}</span>
        <va-icon name="expand_more" class="ml-1" />
      </va-button>
    </template>

    <va-dropdown-content class="p-0 w-64">
      <div class="p-3 border-b border-gray-100 bg-gray-50">
        <span class="text-xs font-bold text-secondary uppercase tracking-wider">Trocar Empresa</span>
      </div>
      <va-list>
        <va-list-item
          v-for="company in companies"
          :key="company.id"
          class="cursor-pointer hover:bg-gray-50 p-3"
          :class="{ 'bg-blue-50': company.id === auth.cmpId }"
          @click="selectCompany(company.id)"
        >
          <va-list-item-section avatar>
            <va-avatar :src="company.logo" :fallback-text="company.name?.charAt(0)" size="small" color="primary" />
          </va-list-item-section>
          <va-list-item-section>
            <va-list-item-label :class="{ 'font-bold': company.id === auth.cmpId }">
              {{ company.name }}
            </va-list-item-label>
          </va-list-item-section>
          <va-list-item-section v-if="company.id === auth.cmpId" icon>
            <va-icon name="check" color="primary" size="small" />
          </va-list-item-section>
        </va-list-item>
      </va-list>
    </va-dropdown-content>
  </va-dropdown>
  <div v-else-if="currentCompanyName" class="flex items-center px-2 text-secondary">
    <va-icon name="business" size="small" class="mr-2" />
    <span class="text-sm font-medium">{{ currentCompanyName }}</span>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vuestic-ui'
import { authStore } from '../../../../stores/auth'
import { usePlanStore } from '../../../../stores/plan'
import { permissionStore } from '../../../../stores/permissions'
import { useGlobalStore } from '../../../../stores/global-store'
import { clearAllStores } from '../../../../stores/storeReset'
import { disconnectAllWebSockets } from '../../../../services/websocketUtils'
import { EventBus } from '../../../../services/eventBus'
import AuthService from '../../../../services/auth/AuthService'

const auth = authStore()
const planStore = usePlanStore()
const perm = permissionStore()
const globalStore = useGlobalStore()
const router = useRouter()
const { init } = useToast()

const companies = computed(() => auth.availableCompanies || [])
const currentCompanyName = computed(() => {
  const current = companies.value.find((c: any) => String(c.id) === String(auth.cmpId))
  return current ? current.name : ''
})

const selectCompany = async (companyId: number) => {
  if (String(companyId) === String(auth.cmpId)) return

  try {
    globalStore.setLoading(true)

    const selectedCompany = companies.value.find((c: any) => String(c?.id) === String(companyId))

    // 0. Disconnect all WebSockets to cancel reconnect timers
    disconnectAllWebSockets()

    // 1. Clear all domain stores
    await clearAllStores()

    // 2. Update active company
    await auth.switchCompany(companyId)

    // 3. Re-fetch profile for new context
    const resp = await AuthService.me()
    auth.updateFunc(resp.data)

    // 4. Load permissions
    await perm.loadMyPermissions()

    // 4.1 Load plan capabilities
    await planStore.fetchPlanSummary(true)

    // 5. Notify components that company has changed
    EventBus.emit('company-changed', companyId)

    const companyLabel = selectedCompany?.name || selectedCompany?.slug || auth.empSlug || String(companyId)
    init({ message: `Empresa alterada para: ${companyLabel}`, color: 'success' })

    // Redirect to dashboard to ensure all components reload with new context
    router.push({ name: 'dashboard' }).then(() => {
      window.location.reload() // Force a full reload to be safe with all stores and websockets
    })
  } catch (error) {
    console.error('Erro ao trocar de empresa:', error)
    init({ message: 'Erro ao trocar de empresa', color: 'danger' })
  } finally {
    globalStore.setLoading(false)
  }
}
</script>

<style scoped>
.company-dropdown {
  display: flex;
  align-items: center;
}
</style>
